import { useForm } from 'react-hook-form';
import { useEffect, useMemo, useState, useRef } from 'react';
import { format } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Form, FormField, FormItem, FormLabel, FormControl } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useUpdateInteraction, useDeleteInteraction, useCreateFollowUp } from '@/hooks/useStakeholderInteractions';
import { useLocalStorageManager } from '@/hooks/useLocalStorageManager';
import type { CRMInteraction, InteractionType } from '@/lib/crm/types';
import { toast } from 'sonner';

interface EditInteractionDialogProps {
  interaction: CRMInteraction | null;
  stakeholders?: any[];
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormData {
  type: InteractionType;
  date: string;
  subject: string;
  outcome: string;
  notes: string;
  attendees: string;
  status: 'pending' | 'completed' | 'cancelled';
  nextFollowUp: string;
}

export function EditInteractionDialog({ 
  interaction,
  stakeholders = [],
  isOpen, 
  onClose, 
  onSuccess
}: EditInteractionDialogProps) {
  const { mutateAsync: updateInteraction, isPending: isUpdating } = useUpdateInteraction();
  const { mutateAsync: deleteInteraction, isPending: isDeleting } = useDeleteInteraction();
  const { mutateAsync: createFollowUp } = useCreateFollowUp();
  const { getStandardValue, setStandardValue, removeStandardValue } = useLocalStorageManager();
  
  const [showFollowUpConfirm, setShowFollowUpConfirm] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<FormData | null>(null);
  
  const draftKey = interaction ? `edit-interaction-draft-${interaction.id}` : '';
  const hasLoadedDraftRef = useRef(false);
  
  // Find stakeholder name
  const stakeholderName = useMemo(() => {
    if (!interaction?.stakeholder_id) return 'Không xác định';
    const stakeholder = stakeholders.find(s => s.id === interaction.stakeholder_id);
    return stakeholder?.name || 'Không xác định';
  }, [interaction, stakeholders]);
  
  const form = useForm<FormData>({
    defaultValues: {
      type: 'Meeting',
      date: '',
      subject: '',
      outcome: '',
      notes: '',
      attendees: '',
      status: 'completed',
      nextFollowUp: ''
    }
  });
  
  useEffect(() => {
    if (isOpen && interaction && draftKey && !hasLoadedDraftRef.current) {
      const draft = getStandardValue<FormData>(draftKey, null as any);
      if (draft) {
        form.reset(draft);
      } else {
        form.reset({
          type: interaction.type,
          date: format(new Date(interaction.date), "yyyy-MM-dd'T'HH:mm"),
          subject: interaction.subject,
          outcome: interaction.outcome || '',
          notes: interaction.notes || '',
          attendees: interaction.attendees || '',
          status: interaction.status || 'completed',
          nextFollowUp: interaction.next_follow_up 
            ? format(new Date(interaction.next_follow_up), 'yyyy-MM-dd') 
            : ''
        });
      }
      hasLoadedDraftRef.current = true;
    }
    
    // Reset flag when dialog closes
    if (!isOpen) {
      hasLoadedDraftRef.current = false;
    }
  }, [isOpen, interaction, draftKey, getStandardValue, form]);
  
  // Auto-save draft
  useEffect(() => {
    if (!isOpen || !draftKey) return;
    const subscription = form.watch((values) => {
      setStandardValue(draftKey, values as FormData);
    });
    return () => subscription.unsubscribe();
  }, [isOpen, form, draftKey, setStandardValue]);
  
  const onSubmit = async (data: FormData) => {
    if (!interaction) return;
    
    // Check if next_follow_up changed and is set
    const hasNextFollowUp = data.nextFollowUp && data.nextFollowUp !== '';
    const isNextFollowUpChanged = data.nextFollowUp !== (interaction.next_follow_up ? format(new Date(interaction.next_follow_up), 'yyyy-MM-dd') : '');
    
    if (hasNextFollowUp && isNextFollowUpChanged) {
      // Show confirmation dialog
      setPendingFormData(data);
      setShowFollowUpConfirm(true);
      return;
    }
    
    // Normal update without follow-up
    await handleUpdate(data);
  };
  
  const handleUpdate = async (data: FormData) => {
    if (!interaction) return;
    
    try {
      await updateInteraction({
        id: interaction.id,
        data: {
          type: data.type,
          date: new Date(data.date).toISOString(),
          subject: data.subject,
          outcome: data.outcome,
          notes: data.notes,
          attendees: data.attendees,
          status: data.status,
          next_follow_up: data.nextFollowUp || undefined
        }
      });
      if (draftKey) removeStandardValue(draftKey);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating interaction:', error);
    }
  };
  
  const handleCreateFollowUp = async () => {
    if (!pendingFormData || !interaction) return;
    
    try {
      // Create follow-up with copied data from interaction
      await createFollowUp({
        stakeholderId: interaction.stakeholder_id,
        date: pendingFormData.nextFollowUp,
        type: pendingFormData.type,
        subject: `Follow-up: ${pendingFormData.subject}`,
        outcome: '',
        attendees: pendingFormData.attendees,
        notes: `Auto-created from interaction on ${format(new Date(interaction.date), 'dd/MM/yyyy')}`,
        status: 'pending',
      });
      
      // Then update the interaction
      await handleUpdate(pendingFormData);
      
      toast.success('Follow-up đã được tạo tự động');
    } catch (error) {
      console.error('Error creating follow-up:', error);
      toast.error('Không thể tạo follow-up');
    } finally {
      setShowFollowUpConfirm(false);
      setPendingFormData(null);
    }
  };
  
  const handleDelete = async () => {
    if (!interaction) return;
    if (!confirm('Bạn có chắc muốn xóa tương tác này?')) return;
    
    await deleteInteraction(interaction.id);
    if (draftKey) removeStandardValue(draftKey);
    onSuccess();
    onClose();
  };
  
  const handleClose = () => {
    if (draftKey) removeStandardValue(draftKey);
    form.reset();
    onClose();
  };
  
  const isPending = isUpdating || isDeleting;
  
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Chỉnh sửa Tương tác</DialogTitle>
          <div className="pt-2 pb-1">
            <p className="text-sm text-muted-foreground">
              Stakeholder: <span className="font-medium text-foreground">{stakeholderName}</span>
            </p>
          </div>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              name="type"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Loại tương tác</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Meeting">Meeting</SelectItem>
                      <SelectItem value="Call">Call</SelectItem>
                      <SelectItem value="Site Visit">Site Visit</SelectItem>
                      <SelectItem value="Delivery">Delivery</SelectItem>
                      <SelectItem value="Approval">Approval</SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            
            <FormField
              name="date"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ngày/Giờ Tương tác *</FormLabel>
                  <FormControl>
                    <Input type="datetime-local" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              name="subject"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Chủ đề</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Nội dung cuộc họp" />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              name="outcome"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Kết quả</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Những điều đã quyết định..." />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              name="notes"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ghi chú</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Chi tiết bổ sung..." />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              name="attendees"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Người tham dự</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Nguyễn Văn A, Trần Thị B..." />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              name="status"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Trạng thái</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            
            <FormField
              name="nextFollowUp"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Follow-up tiếp theo</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <DialogFooter className="gap-2">
              <Button 
                type="button" 
                variant="destructive" 
                onClick={handleDelete} 
                disabled={isPending}
              >
                Xóa
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleClose} 
                disabled={isPending}
              >
                Hủy
              </Button>
              <Button type="submit" disabled={isPending}>
                {isUpdating ? 'Đang lưu...' : 'Lưu'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
        
        <AlertDialog open={showFollowUpConfirm} onOpenChange={setShowFollowUpConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Tạo Follow-up?</AlertDialogTitle>
              <AlertDialogDescription>
                Bạn đã chọn ngày follow-up tiếp theo: <strong>{pendingFormData?.nextFollowUp && format(new Date(pendingFormData.nextFollowUp), 'dd/MM/yyyy')}</strong>
                <br /><br />
                Bạn có muốn tự động tạo một sự kiện follow-up cho ngày này không?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={async () => {
                // Just update interaction without creating follow-up
                if (pendingFormData) {
                  await handleUpdate(pendingFormData);
                }
                setShowFollowUpConfirm(false);
                setPendingFormData(null);
              }}>
                Không, chỉ lưu tương tác
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleCreateFollowUp}>
                Có, tạo follow-up
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
